import React from 'react';
import { Search } from 'lucide-react';
import { CATEGORY_LIST, colorForCategory, textOn, categorySlug } from '@/lib/categories';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';

export default function FilterBar({
    query, onQueryChange,
    category, onCategoryChange,
    sort, onSortChange,
    total,
}) {
    return (
        <div
            data-testid="filter-bar"
            className="flex flex-col gap-6"
        >
            <div className="flex flex-col md:flex-row md:items-center gap-4 md:gap-6">
                {/* search */}
                <div className="relative flex-1">
                    <Search className="absolute left-5 top-1/2 -translate-y-1/2 text-white/50" size={18} />
                    <input
                        data-testid="search-input"
                        type="text"
                        value={query}
                        onChange={(e) => onQueryChange(e.target.value)}
                        placeholder="Search by name or company"
                        className="w-full rounded-full bg-neutral-950 border border-white/15 pl-12 pr-5 py-3.5 text-white placeholder:text-white/40 focus:outline-none focus:border-un-berry focus:ring-1 focus:ring-un-berry font-body"
                    />
                </div>

                {/* sort */}
                <div className="w-full md:w-56 shrink-0">
                    <Select value={sort} onValueChange={onSortChange}>
                        <SelectTrigger
                            data-testid="sort-select"
                            className="rounded-full bg-neutral-950 border-white/15 py-6 text-white focus:ring-un-berry"
                        >
                            <SelectValue placeholder="Sort" />
                        </SelectTrigger>
                        <SelectContent className="bg-neutral-950 text-white border-white/15">
                            <SelectItem value="milestones" data-testid="sort-option-milestones">Most milestones</SelectItem>
                            <SelectItem value="most_awards" data-testid="sort-option-most-awards">Most phases first</SelectItem>
                            <SelectItem value="name" data-testid="sort-option-name">By name (A→Z)</SelectItem>
                            <SelectItem value="category" data-testid="sort-option-category">By phase</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
            </div>

            {/* phase filter pills */}
            <div className="flex flex-wrap gap-2 items-center">
                <FilterChip
                    label="All phases"
                    active={!category}
                    onClick={() => onCategoryChange('')}
                    testid="chip-all"
                />
                {CATEGORY_LIST.map((c) => {
                    const color = colorForCategory(c);
                    const active = category === c;
                    return (
                        <FilterChip
                            key={c}
                            label={c}
                            color={color}
                            active={active}
                            onClick={() => onCategoryChange(active ? '' : c)}
                            testid={`chip-${categorySlug(c)}`}
                        />
                    );
                })}
                {typeof total === 'number' && (
                    <span
                        data-testid="results-count"
                        className="ml-auto self-center text-xs uppercase tracking-[0.2em] text-white/60"
                    >
                        {total} recipient{total === 1 ? '' : 's'}
                    </span>
                )}
            </div>
        </div>
    );
}

function FilterChip({ label, color, active, onClick, testid }) {
    const styleActive = color
        ? { backgroundColor: color, color: textOn(color), borderColor: color }
        : { backgroundColor: '#FFFFFF', color: '#000000', borderColor: '#FFFFFF' };
    // Small color dot when inactive, to hint at the phase color.
    return (
        <button
            type="button"
            data-testid={testid}
            onClick={onClick}
            className={`inline-flex items-center gap-2 rounded-full px-3.5 py-2 text-[11px] font-semibold uppercase tracking-[0.1em] border transition-colors ${
                active
                    ? ''
                    : 'border-white/20 text-white/80 hover:border-white/60 hover:text-white'
            }`}
            style={active ? styleActive : undefined}
        >
            {color && !active && (
                <span
                    aria-hidden
                    className="inline-block h-2 w-2 rounded-full"
                    style={{ backgroundColor: color }}
                />
            )}
            {label}
        </button>
    );
}
